import { createSlice, createSelector, createEntityAdapter } from '@reduxjs/toolkit';
import { RootState } from '../store';
import { getSharedTeams, getTeamInvites, getTeams, teamAcceptInvite } from '../actions/teamActions';

export const teamAdapter = createEntityAdapter();

export type Team = {
    id: number;
    name: string;
    photo: string | null;
    user_id: number;
};

export type Invitation = {
    id: number;
    invitator: string;
    photo: string | null;
    team_id: number;
    team_name: string;
};

export type TeamMember = {
    id: number;
    user_id: number;
    name: string;
    email: string;
    photo: string | null;
    icon: number;
    phone: string;
    language: string;
};
export type PendingMember = {
    id: number;
    user_id: number;
    name: string;
    email: string;
    photo: string | null;
    icon: number;
    phone: string;
    language: string;
};

const INITIAL_STATE = {
    team: [],
    teams: [] as Team[],
    sharedTeams: [] as Team[],
    teamPending: [],
    activeTeam: null,
    currentTeam: undefined,
    allTeamPositions: [],
    selectedTeamMember: null,
    invites: [] as Invitation[],
    pendingMembers: [],
    teamMembersUpdater: 0,
    teamPanelVisible: false,
};

const teamReducer = createSlice({
    name: 'team',
    initialState: teamAdapter.getInitialState(INITIAL_STATE),
    reducers: {
        setActiveTeam(state, action) {
            state.activeTeam = action.payload;
        },
        setCurrentTeam(state, action) {
            state.currentTeam = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(getTeams.fulfilled, (state, action) => {
                state.teams = action.payload;
            })
            .addCase(getTeams.rejected, () => {})
            .addCase(getTeamInvites.fulfilled, (state, action) => {
                state.invites = action.payload;
            })
            .addCase(getTeamInvites.rejected, () => {})
            .addCase(teamAcceptInvite.fulfilled, (state, action) => {
                state.teams = action.payload.data;
                state.invites = state.invites.filter((inv) => inv.id !== action.payload.id);
            })
            .addCase(teamAcceptInvite.rejected, () => {})
            .addCase(getSharedTeams.fulfilled, (state, action) => {
                state.sharedTeams = action.payload;
            })
            .addCase(getSharedTeams.rejected, () => {});
    },
});

export const { setActiveTeam, setCurrentTeam } = teamReducer.actions;

export default teamReducer.reducer;

const teamState$ = (s: RootState) => s.team;

export const rawTeams$ = createSelector(teamState$, (s) => s.teams);

export const sortedTeams$ = createSelector(
    rawTeams$,
    (teams) => [...teams]?.sort((a, b) => (a.name > b.name ? 1 : b.name > a.name ? -1 : 0)),
);

export const rawSharedTeams$ = createSelector(teamState$, (s) => s.sharedTeams);

export const allTeams$ = createSelector(rawTeams$, rawSharedTeams$, (teams, sharedTeams) => {
    return teams.concat(sharedTeams);
});

export const sortedSharedTeams$ = createSelector(
    rawSharedTeams$,
    (teams) => [...teams]?.sort((a, b) => (a.name > b.name ? 1 : b.name > a.name ? -1 : 0)),
);

export const activeTeamId$ = createSelector(teamState$, (s) => s.activeTeam);

export const teamsSections$ = createSelector(
    sortedTeams$,
    rawSharedTeams$,
    activeTeamId$,
    (teams, sharedTeams, activeTeamId) => [
        {
            title: 'teams.memberOf',
            data: teams ?? [],
            // data: teams?.filter((team) => team.id != activeTeamId) ?? [],
        },
        {
            title: 'teams.guestOf',
            data: sharedTeams ?? [],
            // data: sharedTeams?.filter((team) => team.id != activeTeamId),
        },
    ],
);

export const currentTeam$ = createSelector(
    rawTeams$,
    rawSharedTeams$,
    activeTeamId$,
    (teams, sharedTeams, activeTeamId) => {
        return [...teams, ...sharedTeams].find((t) => t.id === activeTeamId);
    },
);
